import {
  FloatingFocusManager,
  Placement,
  useClick,
  useDismiss,
  useFloating,
  useInteractions
} from '@floating-ui/react';
import * as React from 'react';
import { useAppContext } from '../../../components/app-context/app-context.tsx';
import { useId, useRef, useState } from 'react';
import {
  floatingMiddleware,
  useUpdateFloatingPositionWhileOpen
} from '../../../utils/floating-content-utils.tsx';
import { ariaDialogPattern, ariaDisclosurePattern } from '../../../utils/aria.tsx';
import { classList } from '../../../utils/string.ts';
import composeRefs from '../../../utils/compose-refs.ts';
import { floatingArrow } from '../floating-arrow/floating-arrow.tsx';

export type ToggletipMaxWidth = 'small' | 'medium' | 'large';
export type ToggletipTextColor = 'normal' | 'muted';

const isButtonElement = (
  element: unknown
): element is HTMLButtonElement | HTMLAnchorElement | HTMLInputElement =>
  element instanceof HTMLElement && element.nodeName === 'BUTTON';

export type ToggletipOptions = {
  content: React.ReactNode;
  contentClassName?: string;
  /**
   * Event that can be used to react on the opening of the toggletip.
   */
  onOpen?: () => void;
  /**
   * Event that can be used to react on the closing of the toggletip.
   */
  onClose?: () => void;
  /**
   * The visual placement of the toggletip relative to its reference element.
   * Defaults to top.
   */
  placement?: Placement;
  /**
   * Defaults to 'p-2'.
   */
  paddingClass?: string;
  /**
   * Defaults to 'max-w-72'.
   */
  maxWidthClass?: string;
  /**
   * Avoid the effort of introducing custom css by using this property.
   * Defaults to 'muted'.
   */
  textColor?: ToggletipTextColor;
  /**
   * If true, focus will be trapped inside and the toggletip will be handled as a dialog.
   */
  modal?: boolean;
};

/**
 * ## What is a toggletip?
 *
 * A toggletip, sometimes also called "popover", "infotip" or "disclosure tooltip", is a [disclosure pattern](https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/), where a button is used to
 * control floating content. It looks visually similar to a tooltip, but has different use cases.
 *
 * Toggletips differ from tooltips in multiple regards:
 *
 * 1. A toggletip can be toggled via click.
 * 2. A toggletip is always connected to a button, which toggles the floating content (disclosure pattern).
 * 3. Unlike a tooltip, a toggletip does not describe an interactive element.
 * 4. A toggletip may contain rich content and interactive elements.
 *
 * ## Links
 *
 * Toggletips are a usual pattern in the web. So, our implementation is inspired from various articles and research.
 * To get a fundamental understanding, these links should provide a good start.
 *
 * - [Tooltips in the time of WCAG 2.1](https://sarahmhigley.com/writing/tooltips-in-wcag-21/)
 * - [Content on hover & focus](https://accessuse.eu/en/Content-hover-focus.html)
 * - [Tooltips & Toggletips](https://inclusive-components.design/tooltips-toggletips/)
 * - [Disclosure Widgets](https://adrianroselli.com/2020/05/disclosure-widgets.html)
 * - [Ebay MIND Patterns Infotip](https://ebay.gitbook.io/mindpatterns/disclosure/infotip)
 * - [Tooltip alternatives](https://primer.style/design/accessibility/tooltip-alternatives/)
 *
 * ## Side notes
 *
 * - On [this page](https://codepen.io/aardrian/pen/dyowpJj?editors=0100), it says:
 *
 *   > If there is no interactive content, then the entire container must be able to accept focus so a keyboard-only user can scroll to see it all."
 *
 *   This is currently not applicable for us, because we don't have floating elements with scrollable content.
 *
 * -  `role=„has-popup“` is not applicable for us, because our toggletips don't have the role menu, listbox, tree, grid or dialog, which is a requirement:
 *    [Accessible Rich Internet Applications (WAI-ARIA) 1.3](https://w3c.github.io/aria/#aria-haspopup)
 *    Also See Semantics to avoid [Tooltips in the time of WCAG 2.1 | Sarah Higley](https://sarahmhigley.com/writing/tooltips-in-wcag-21/)
 *
 * -  `role="aria-live"` may be tempting but has drawbacks (see [Tooltips in the time of WCAG 2.1 | Sarah Higley](https://sarahmhigley.com/writing/tooltips-in-wcag-21/))
 *
 * - Why not open the toggletip on hover or focus?
 *   See [Disclosure Widgets](https://adrianroselli.com/2020/05/disclosure-widgets.html#OtherHovcus):
 *   > Generally you want to avoid revealing the hidden content when a control receives focus or is hovered. Tool-tips are a valid exception, but in most other cases this will produce usability challenges.
 *   > If you pursue it, ensure that there is no nested interactive content, as a user may be unable to access it when only hovering. Unless the revealed content persists.
 *   > Which means it will also need to be dismissable. See [Success Criterion 1.4.13: Content on Hover or Focus (AA, WCAG 2.1)](https://www.w3.org/WAI/WCAG21/Understanding/content-on-hover-or-focus.html) for more detail.
 *
 *   Also a good explanation, why it might not be a good idea, from [Your tooltips are bogus](https://heydonworks.com/article/your-tooltips-are-bogus/):
 *   > Even if you consider the showing of the tooltip an interactive 'action', it happening on focus makes little sense, especially to unsighted screen reader users who won't know anything has happened.
 *
 *   Hover and focus must go together, so do not split this into two properties (see https://www.w3.org/WAI/WCAG21/Understanding/content-on-hover-or-focus.html)
 *   > Content which can be triggered via pointer hover should also be able to be triggered by keyboard focus.
 */
export const useToggletip = (
  options: ToggletipOptions
): {
  /**
   * Should be placed directly after the reference button.
   * This is important, so that focus and screen readers can directly enter the floating content.
   */
  floatingContent: React.ReactNode | undefined;
  /**
   * Should be assigned to the reference element.
   * It must be a button component, that supports all props of native <button> elements
   * (especially events like onClick, onHover, onFocus, etc. and aria-attributes).
   */
  buttonProps: (userProps?: React.HTMLProps<Element> | undefined) => Record<string, unknown>;
  buttonRef: React.Ref<HTMLButtonElement>;
  toggle: (open: boolean) => void;
} => {
  const {
    placement = 'top',
    paddingClass = 'p-2',
    maxWidthClass = 'max-w-72',
    modal = false
  } = options;
  const { appContext } = useAppContext();

  const [open, setOpen] = useState(false);

  const arrowRef = useRef(null);
  const floatingRef = useRef<HTMLDivElement>(null);
  const floatingId = useId();

  const toggle = (newOpen: boolean) => {
    if (newOpen) {
      options.onOpen?.();
    } else {
      options.onClose?.();
    }
    setOpen(newOpen);
  };

  const {
    x,
    y,
    refs,
    strategy,
    context,
    update,
    placement: activePlacement,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} }
  } = useFloating({
    placement,
    open,
    onOpenChange: toggle,
    strategy: 'fixed', // so that overflow:hidden of parents is no problem
    middleware: floatingMiddleware({ arrowRef })
  });

  useUpdateFloatingPositionWhileOpen({ open, update });

  const { buttonProps: disclosureButtonProps } = ariaDisclosurePattern({
    collapsableContentId: floatingId,
    expanded: open
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, { toggle: true }),
    useDismiss(context, {})
  ]);
  const contentClasses = classList(
    'bg-sk-grey-0 shadow-sk-grey-500 rounded-md text-left text-sm font-normal break-normal whitespace-normal shadow-md z-100',
    paddingClass,
    maxWidthClass,
    !open && 'u-hidden',
    options.contentClassName
  );

  const floatingContent = open ? (
    <FloatingFocusManager context={context} modal={modal} returnFocus={true}>
      <div
        id={floatingId}
        ref={composeRefs<HTMLDivElement>(floatingRef, refs.setFloating)}
        className={contentClasses}
        style={{
          position: strategy,
          top: y ?? 0,
          left: x ?? 0
        }}
        {...getFloatingProps(modal ? ariaDialogPattern({ modal }).dialogProps : undefined)}
      >
        {options.content}
        {floatingArrow({ arrowRef, placement: activePlacement, x: arrowX, y: arrowY })}
      </div>
    </FloatingFocusManager>
  ) : undefined;

  const buttonProps: typeof getReferenceProps = props =>
    getReferenceProps({ ...disclosureButtonProps, ...props });

  const ensureButtonElement = (child: unknown) => {
    if (child && !isButtonElement(child)) {
      appContext.logger.error(
        `Wrong usage of Toggletip component. Expected button element, but got ${
          child instanceof HTMLElement ? child.nodeName : ''
        }, ${child}`
      );
    }
  };

  return {
    floatingContent,
    buttonProps,
    buttonRef: composeRefs<HTMLButtonElement>(refs.setReference, ensureButtonElement),
    toggle
  };
};

export type ToggletipProps = ToggletipOptions & {
  button: (
    props: React.DetailedHTMLProps<React.HTMLAttributes<HTMLButtonElement>, HTMLButtonElement>
  ) => React.ReactElement;
};

/**
 * Standalone component for toggletips.
 *
 * It alters the children, so there must be exactly one child and it must be a button.
 * The child can be any component, as long as it supports all attributes of a native <button> (see ToggletipOptions['children']).
 */
export const Toggletip: React.FunctionComponent<ToggletipProps> = props => {
  const { floatingContent, buttonProps, buttonRef } = useToggletip(props);

  return (
    <>
      {props.button(buttonProps({ ref: buttonRef }))}
      {floatingContent}
    </>
  );
};
